import classNames from 'classnames'
import { FC, forwardRef, ReactNode, ElementType, ComponentPropsWithRef, MouseEventHandler } from 'react'
import { MenuItem as HeadlessMenuItem } from '@headlessui/react'
import { CheckIcon } from '@heroicons/react/24/outline'

import { Link, LinkNavigationProps } from '@/components/Link'

type ItemProps = LinkNavigationProps & {
    className?: string
    Icon?: ElementType<ComponentPropsWithRef<'svg'>>
    iconClassName?: string
    checked?: boolean
    disabled?: boolean
    small?: boolean
    children?: ReactNode
    onClick?: MouseEventHandler<HTMLDivElement>
}

export const Item: FC<ItemProps> = forwardRef<HTMLDivElement, ItemProps>(function Item(
    { className, Icon, iconClassName, checked, disabled, small, children, onClick, ...linkProperties },
    ref
) {
    return (
        <HeadlessMenuItem
            ref={ref}
            as="div"
            disabled={disabled}
            className={classNames('whitespace-nowrap')}
            onClick={onClick}
        >
            {({ focus, disabled }) => (
                // @ts-expect-error its fine, we must deactivate onClick for link
                <Link
                    nonLinkElement="div"
                    {...linkProperties}
                    onClick={undefined}
                    className={classNames(
                        className,
                        'text-primary group flex w-full cursor-default select-none items-center rounded-md active:bg-neutral-300 dark:active:bg-neutral-900',
                        {
                            'bg-neutral-200 dark:bg-neutral-700': focus,
                            'pointer-events-none opacity-60': disabled,
                            'px-2 py-1.5 text-xs': small,
                            'px-3 py-2': !small,
                        }
                    )}
                >
                    {typeof checked === 'boolean' && (
                        <div className="shrink-0">
                            <div className={classNames('mr-2.5 flex items-center justify-center')}>
                                <div
                                    className={classNames('rounded border', {
                                        'bg-accent-500 border-accent-500 dark:border-accent-500 text-white': checked,
                                        'border-neutral-300 bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-800 ':
                                            !checked,
                                    })}
                                >
                                    {checked ? (
                                        <CheckIcon className={classNames({ 'size-4': !small, 'size-3': small })} />
                                    ) : (
                                        <div className={classNames({ 'size-4': !small, 'size-3': small })} />
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    {Icon && (
                        <div className="shrink-0">
                            <Icon
                                className={classNames(iconClassName, 'group-hover:text-primary mr-2.5 h-5 w-5', {
                                    'text-secondary': !iconClassName,
                                })}
                            />
                        </div>
                    )}
                    {children}
                </Link>
            )}
        </HeadlessMenuItem>
    )
})
